"use client";

/**
 * Day Chip
 * Presentational day tab for the calendar's day strip: the weekday over "Sep 15, 2026", with
 * an active (selected) treatment driven by the page primary color. Stateless; the parent owns
 * selection and layout.
 */

interface DayChipProps {
  /** Short weekday, e.g. "Mon". */
  weekdayShort: string;
  /** Month, day and year, e.g. "Sep 15, 2026". */
  dateLabel: string;
  /** Whether this chip is the selected day. */
  isActive: boolean;
  /** Primary color used as the active chip background. */
  activeColor: string;
  /** Text color to use on the active chip (contrast-correct for `activeColor`). */
  activeTextColor: string;
  /** Accessible label for the chip, e.g. "Monday, August 12". */
  ariaLabel: string;
  /** Smaller hit target for dense fixed calendar chrome. */
  compact?: boolean;
  onClick: () => void;
}

/**
 * A single selectable day cell in a day strip.
 *
 * @param props - See DayChipProps.
 * @returns The day chip button.
 */
export function DayChip({
  weekdayShort,
  dateLabel,
  isActive,
  activeColor,
  activeTextColor,
  ariaLabel,
  compact = false,
  onClick,
}: DayChipProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      aria-label={ariaLabel}
      className={`flex shrink-0 touch-manipulation flex-col items-center justify-center whitespace-nowrap rounded-lg transition-colors ${
        compact ? "min-h-9 px-2.5 py-1" : "min-h-11 px-3 py-1.5"
      } ${isActive ? "" : "text-gray-700 hover:bg-gray-100"}`}
      style={isActive ? { backgroundColor: activeColor, color: activeTextColor } : undefined}
    >
      <span className="text-xs font-medium leading-tight opacity-80">{weekdayShort}</span>
      <span className={`font-semibold leading-tight tabular-nums ${compact ? "text-xs" : "text-sm"}`}>{dateLabel}</span>
    </button>
  );
}
